import { ArrowLeft, ArrowRight } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Location, LocationDescriptor } from 'history';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';
import { useExperienceModuleContext } from 'modules/experience/context';
import { ExperienceEntityType } from 'modules/experience/types';

import { EntitySelector } from './EntitySelector';
import { MobileHeader } from './MobileHeader';
import { VenueSelector } from './VenueSelector';

type PageHeaderProps = {
  title?: string | ReactNode;
  backLink?: string | ((location: Location) => LocationDescriptor);
  backLinkText?: string | ReactNode;
  children?: React.ReactNode;
};

export const PageHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: space-between;
  padding: 24px 24px 8px;
  min-height: 88px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  gap: 16px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 24px;
    min-height: unset;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-height: 36px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    min-height: 24px;
  }
`;

const HeadingLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PageHeading = styled.h1`
  ${typographyCss.Heading4}
  margin: 0;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Heading6}
  }
`;

const StyledBackLink = styled(Link)`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${cssThemedValue('textSecondary')};
  cursor: pointer;
  margin-block-end: 4px;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  fill: ${cssThemedValue('textSecondary')};
`;

const StyledArrowRight = styled(ArrowRight)`
  fill: ${cssThemedValue('textSecondary')};
`;

const BackLinkText = styled.span`
  ${typographyCss.Body3}
  font-weight: 500;
`;

const SmallerPageHeading = styled.h1`
  ${typographyCss.Heading5}
  margin: 0;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Heading6}
  }
`;

const SubHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 24px;
`;

export const PageHeader = ({ title, children, backLink, backLinkText }: PageHeaderProps) => {
  const { activeNavigation, experienceEntity } = useExperienceModuleContext();
  const isMobile = useIsMobile();
  const { cbDirection } = useTheme();
  const label = title ? (
    title
  ) : activeNavigation?.intlKey ? (
    <FormattedMessage id={activeNavigation.intlKey} />
  ) : (
    <FormattedMessage id="navigation.experience" />
  );
  const shouldRenderSelector = experienceEntity.experienceEntityType !== ExperienceEntityType.VENUE;

  const Heading = backLink && backLinkText ? SmallerPageHeading : PageHeading;

  return (
    <>
      {!isMobile && (
        <PageHeaderContainer>
          <HeadingContainer>
            <HeadingLabelContainer>
              {backLink && backLinkText && (
                <StyledBackLink to={backLink}>
                  {cbDirection === 'ltr' ? <StyledArrowLeft width="12px" /> : <StyledArrowRight width="12px" />}
                  <BackLinkText>{backLinkText}</BackLinkText>
                </StyledBackLink>
              )}
              <Heading>{label}</Heading>
            </HeadingLabelContainer>
            {shouldRenderSelector && (activeNavigation?.isVenueSpecific ? <VenueSelector /> : <EntitySelector />)}
          </HeadingContainer>
          {!!children && <div>{children}</div>}
        </PageHeaderContainer>
      )}
      {isMobile && (
        <>
          <MobileHeader backLink={backLink} title={label} />
          {(!!children || shouldRenderSelector) && (
            <SubHeaderSection>
              {shouldRenderSelector && (activeNavigation?.isVenueSpecific ? <VenueSelector /> : <EntitySelector />)}
              {!!children && <div>{children}</div>}
            </SubHeaderSection>
          )}
        </>
      )}
    </>
  );
};
